import React, { useState } from 'react'
import GooglePlacesAutocomplete, { geocodeByAddress } from 'react-google-places-autocomplete'
import { createUseStyles } from 'react-jss'

import PropertyTypeMultiSelect from 'components/PropertyTypeMultiSelect'
import GeneralInput from 'components/GeneralInput'
import RoundedButtonsRow from 'components/RoundedButtonsRow'
import StepButtons from '../../../../components/StepButtons'

import { useCommonStyles } from 'styles/globalStyles'
import { PropertyAnnouncement } from 'types/propertyAnnouncement'

import { getFormattedAddressDetails } from 'helpers/address'
import { STEPS } from 'constants/announcementSteps'

type Props = {
  setPropertyType: (type: string) => void
  propertyTypes: string[]
  property: PropertyAnnouncement
  onClickSave: (payload: Partial<PropertyAnnouncement>, nextStep: string) => void
  onPressBack: () => void
}

const errorStringMap = {
  area: 'Área',
  zip_code: 'CEP',
  city: 'Cidade',
  state: 'Estado',
  street: 'Rua',
  neighbourhood: 'Bairro',
}

const requiredFields = Object.keys(errorStringMap)

const PropertyInfo = (props: Props) => {
  const [info, setInfo] = useState(props.property.info)
  const [address, setAddress] = useState(props.property.address)
  const [location, setLocation] = useState(props.property.location)
  const [locationDeltas, setLocationDeltas] = useState(props.property.location_deltas)
  const [errors, setErrors] = useState({
    area: '',
    zip_code: '',
    city: '',
    state: '',
    street: '',
    neighbourhood: '',
  })

  const commonClasses = useCommonStyles(props)
  const classes = useStyles(props)

  const cleanError = (key: string) => {
    setErrors({ ...errors, [key]: '' })
  }

  const setPropertyInfo = (infoName: string, value: any) => {
    if (requiredFields.includes(infoName)) {
      cleanError(infoName)
    }
    setInfo({ ...info, [infoName]: value })
  }

  const setPropertyAddress = (addressProperty: string, value: any) => {
    if (requiredFields.includes(addressProperty)) {
      cleanError(addressProperty)
    }
    setAddress({ ...address, [addressProperty]: value })
  }

  const onSelectAddress = (location) => {
    geocodeByAddress(location.label).then((results) => {
      const { coordinates, ...formattedAddress } = getFormattedAddressDetails(results[0])
      setAddress({ ...address, ...formattedAddress })
      setLocation({ latitude: coordinates?.latitude || 0, longitude: coordinates?.longitude || 0 })
      setLocationDeltas({
        latitude: coordinates?.latitudeDelta || 0,
        longitude: coordinates?.longitudeDelta || 0,
      })
    })
  }

  const checkIfHasErrors = () => {
    let hasErrors = false
    let updatedErrors = errors
    requiredFields.forEach((key) => {
      if (address[key] !== undefined && !`${address[key]}`.trim()) {
        updatedErrors = { ...updatedErrors, [key]: `${errorStringMap[key]} é um campo obrigatório` }
        hasErrors = true
      }

      if (info[key] !== undefined && !info[key]) {
        updatedErrors = { ...updatedErrors, [key]: `${errorStringMap[key]} é um campo obrigatório` }
        hasErrors = true
      }
    })
    setErrors(updatedErrors)
    return hasErrors
  }

  const onClickSave = () => {
    const hasErrors = checkIfHasErrors()

    if (hasErrors) {
      return
    }

    const payload = {
      info,
      address,
      location,
      location_deltas: locationDeltas,
    }

    props.onClickSave(payload, STEPS.VALUES)
  }

  return (
    <div>
      <PropertyTypeMultiSelect onClick={props.setPropertyType} propertyTypes={props.propertyTypes} />
      <div className={classes.propertyAreaContainer}>
        <GeneralInput
          label='Área do imóvel'
          value={`${info.area}`}
          handleChange={(e: any) => setPropertyInfo('area', Number(e.target.value))}
          error={errors.area}
        />
      </div>

      {/* =================== Info ================== */}
      <div className={commonClasses.roundButtonsContainer}>
        <span className={commonClasses.roundButtonLabel}>Quartos</span>
        <RoundedButtonsRow
          type='rooms'
          hasDoesntMatter={false}
          min={1}
          max={5}
          selectedValue={info.rooms}
          setValue={(label, number) => setPropertyInfo(label, number)}
        />
      </div>
      <div className={commonClasses.roundButtonsContainer}>
        <span className={commonClasses.roundButtonLabel}>Banheiros</span>
        <RoundedButtonsRow
          type='bathrooms'
          hasDoesntMatter={false}
          min={1}
          max={5}
          selectedValue={info.bathrooms}
          setValue={(label, number) => setPropertyInfo(label, number)}
        />
      </div>
      <div className={commonClasses.roundButtonsContainer}>
        <span className={commonClasses.roundButtonLabel}>Vagas de garagem</span>
        <RoundedButtonsRow
          type='car_slots'
          hasDoesntMatter={false}
          hasPlusSign
          min={0}
          max={4}
          selectedValue={info.car_slots}
          setValue={(label, number) => setPropertyInfo(label, number)}
        />
      </div>

      {/* ===================== ADDRESS ================== */}
      <div className={classes.autocompleteContainer}>
        <p style={{ marginBottom: 16 }}>Procure o endereço</p>
        <GooglePlacesAutocomplete
          selectProps={{
            onChange: onSelectAddress,
            placeholder: 'Insira o endereço da propriedade',
            loadingMessage: () => 'Carregando...',
            noOptionsMessage: () => '',
          }}
        />
      </div>

      <GeneralInput
        label='CEP'
        value={address.zip_code}
        handleChange={(e) => setPropertyAddress('zip_code', e.target.value)}
        error={errors.zip_code}
      />
      <GeneralInput
        label='Cidade'
        value={address.city}
        handleChange={(e) => setPropertyAddress('city', e.target.value)}
        error={errors.city}
      />
      <GeneralInput
        label='Nome da rua'
        value={address.street}
        handleChange={(e) => setPropertyAddress('street', e.target.value)}
        error={errors.street}
      />
      <GeneralInput
        label='Bairro'
        value={address.neighbourhood.trim()}
        handleChange={(e) => setPropertyAddress('neighbourhood', e.target.value)}
        error={errors.neighbourhood}
      />

      <GeneralInput
        handleChange={(e) => setPropertyAddress('number', e.target.value)}
        value={address.number}
        label='Número'
      />
      <GeneralInput
        label='Complemento'
        value={address.complement}
        handleChange={(e) => setPropertyAddress('complement', e.target.value)}
      />

      <StepButtons onPressNext={onClickSave} onPressBack={props.onPressBack} />
    </div>
  )
}

const useStyles = createUseStyles({
  propertyAreaContainer: {
    margin: '16px 0 32px',
  },
  autocompleteContainer: {
    margin: '16px 0 32px',
  },
})

export default PropertyInfo
